import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'

import Dialog from './Dialog'
import DatePicker from './DatePicker'
import InputField from './InputField'
import GridIconButton from './GridIconButton'
import SortableTableHead from './SortableTableHead'
import GridCircularProgress from './GridCircularProgress'
import { colours, titlesRendiconto } from '../settings/settings'
import { sortAlphabetically, sortVociRendiconto } from '../hooks/sort-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetCalendariManyQuery,
  useGetCampionatiQuery,
  useGetIscrizioniBySocietaQuery,
  useGetSpostamentiBySocietaQuery,
} from '../hooks/api-slice'
import { sumFun, formatEuro } from '../hooks/utils-functions'

const DialogRendiconto = ({
  open,
  handleClose,
  triggerFunction,
  rendicontoInit,
  idGareArbitro,
  idGareRitardo,
  mode,
}) => {
  const { admin } = useSelector(state => state.home.infoUtente)
  const { data: campionati } = useGetCampionatiQuery()
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !admin,
  })

  const [rendiconto, setRendiconto] = useState()

  useEffect(() => {
    if (open) setRendiconto(rendicontoInit)
  }, [open, rendicontoInit])

  const codiceSocieta = Number(rendiconto?.codiceSocieta)
  const nomeSocieta = affiliazioniLight?.find(
    a => a.codiceSocieta === codiceSocieta,
  )?.nomeSocieta

  const { data: societaIscrizioni, isFetching: loadingIscrizioni } =
    useGetIscrizioniBySocietaQuery(codiceSocieta, { skip: !codiceSocieta })
  const { data: societaGareArbitro, isFetching: loadingGareArbitro } =
    useGetCalendariManyQuery(idGareArbitro, { skip: !idGareArbitro?.length })
  const { data: societaGareRitardo, isFetching: loadingGareRitardo } =
    useGetCalendariManyQuery(idGareRitardo, { skip: !idGareRitardo?.length })
  const { data: societaSpostamenti, isFetching: loadingSpostamenti } =
    useGetSpostamentiBySocietaQuery(codiceSocieta, { skip: !codiceSocieta })

  const isView = mode === 'view'
  const isUpdate = mode === 'update'

  let columnsQuota = [
    { label: 'Causale', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  let columnsIscrizioni = [
    { label: 'Campionato', sortable: false },
    { label: 'Categoria', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  let columnsSpostamenti = [
    { label: 'Campionato', sortable: false },
    { label: 'Categoria', sortable: false },
    { label: 'Gara', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  let columnsGareArbitro = [
    { label: 'Campionato', sortable: false },
    { label: 'Categoria', sortable: false },
    { label: 'Gara', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  let columnsRisultatiRitardo = [
    { label: 'Campionato', sortable: false },
    { label: 'Categoria', sortable: false },
    { label: 'Gara', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  let columnsSanzioni = [
    { label: 'Descrizione', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  let columnsAcconti = [
    { label: 'Descrizione', sortable: false },
    { label: 'Importo', sortable: false },
    { label: 'Note', sortable: false },
    { label: 'Pagamento', sortable: false },
  ]

  const loadingSanzioni = false
  const loadingAcconti = false
  const [newSanzione, setNewSanzione] = useState()
  const [newAcconto, setNewAcconto] = useState()

  const nCampionato = x => campionati?.find(c => c.codice === x)?.nome

  const totTotaleDovuto = () =>
    sumFun(
      rendiconto?.voci
        ?.filter(v => v.tipologia !== 'acconto')
        ?.map(v => v.importo),
    ) || 0
  const totTotalePagato = () =>
    sumFun(rendiconto?.voci?.filter(v => v.datePaid)?.map(v => v.importo)) || 0

  const rendicontoQuota = rendiconto?.voci?.filter(
    v => v.tipologia === 'quota-segreteria',
  )

  const rendicontoIscrizioni = rendiconto?.voci
    ?.filter(v => v.tipologia === 'iscrizione')
    ?.map(v => {
      const vInfo = societaIscrizioni?.find(s => s._id === v.idSquadra)
      return {
        ...v,
        indiceCampionato: campionati?.find(
          c => c.codice === vInfo?.codiceCampionato,
        )?.indice,
        codiceCampionato: vInfo?.codiceCampionato,
        categoria: vInfo?.categoria,
      }
    })
    ?.sort(sortVociRendiconto)

  const rendicontoSpostamenti = rendiconto?.voci
    ?.filter(v => v.tipologia === 'spostamento')
    ?.map(v => {
      const vInfo = societaSpostamenti?.find(s => s._id === v.idSpostamento)
      return {
        ...v,
        indiceCampionato: campionati?.find(
          c => c.codice === vInfo?.codiceCampionato,
        )?.indice,
        codiceCampionato: vInfo?.codiceCampionato,
        categoria: vInfo?.categoria,
        numeroGara: vInfo?.numeroGara,
      }
    })
    ?.sort(sortVociRendiconto)

  const rendicontoGareArbitro = rendiconto?.voci
    ?.filter(
      v => v.tipologia === 'needsReferee' || v.tipologia === 'requiresReferee',
    )
    ?.map(v => {
      const vInfo = societaGareArbitro?.find(s => s._id === v.idGara)
      return {
        ...v,
        indiceCampionato: campionati?.find(
          c => c.codice === vInfo?.codiceCampionato,
        )?.indice,
        codiceCampionato: vInfo?.codiceCampionato,
        categoria: vInfo?.categoria,
        numeroGara: vInfo?.numeroGara,
      }
    })
    ?.sort(sortVociRendiconto)

  const rendicontoRisultatiRitardo = rendiconto?.voci
    ?.filter(v => v.tipologia === 'risultato-ritardo')
    ?.map(v => {
      const vInfo = societaGareRitardo?.find(s => s._id === v.idGara)
      return {
        ...v,
        indiceCampionato: campionati?.find(
          c => c.codice === vInfo?.codiceCampionato,
        )?.indice,
        codiceCampionato: vInfo?.codiceCampionato,
        categoria: vInfo?.categoria,
        numeroGara: vInfo?.numeroGara,
      }
    })
    ?.sort(sortVociRendiconto)

  const rendicontoSanzioni = rendiconto?.voci
    ?.filter(v => v.tipologia === 'sanzione')
    ?.sort((a, b) => sortAlphabetically(a.descrizione, b.descrizione))

  const rendicontoAcconti = rendiconto?.voci
    ?.filter(v => v.tipologia === 'acconto')
    ?.sort((a, b) => sortAlphabetically(a.descrizione, b.descrizione))

  useEffect(() => {
    if (!open) {
      setRendiconto()
      setNewSanzione()
      setNewAcconto()
    }
  }, [open])

  const fTitle = title => (
    <Grid size={12} marginTop={3}>
      <center>
        <b>{title}</b>
      </center>
    </Grid>
  )

  const widthProps1 = { width: '10vw' }
  const widthProps2 = { width: '20vw' }
  const fStyleProps = (x, i) => {
    return {
      style: {
        borderBottomColor:
          i < x.length - 1 &&
          x[i].codiceCampionato !== x[i + 1].codiceCampionato &&
          colours.greyVeryDark,
      },
    }
  }

  const setRendVoce = (x, r) =>
    setRendiconto(prev => ({
      ...prev,
      voci: prev?.voci?.map(v => (v._id === r._id ? { ...v, ...x } : v)),
    }))

  const rInputField = (r, field, aDispatch, type, errorFunc) =>
    type === 'date' ? (
      isView ? (
        r[field]
      ) : (
        <DatePicker
          value={r[field]}
          onChange={x => aDispatch({ [field]: x || undefined })}
          readOnly={isView}
          variant='standard'
        />
      )
    ) : (
      <InputField
        align='center'
        variant='standard'
        placeholder={type === 'number' ? '0.00' : ''}
        value={
          (type === 'number' &&
            (typeof r[field] === 'number' ? r.importo : undefined)) ||
          r[field]
        }
        onChange={x =>
          aDispatch({ [field]: type === 'number' ? +x?.toFixed(2) : x })
        }
        errorFunc={errorFunc}
        type={type}
        readOnly={isView}
      />
    )

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={() => triggerFunction(isUpdate ? rendiconto : undefined)}
      title={titlesRendiconto.titleRendiconto}
      textUndo={isView || !admin ? 'Chiudi' : 'Annulla'}
      textConfirm={!isView && admin && 'Salva'}
      maxWidth='lg'
      stretchHeight
    >
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {admin && (
          <Grid size={{ xs: 12, md: 6 }}>
            <center>
              {codiceSocieta} - <b>{nomeSocieta}</b>
            </center>
          </Grid>
        )}
        <Grid
          container
          spacing={2}
          marginBottom={3}
          alignItems='center'
          sx={{ width: '100%' }}
        >
          <Grid size={{ xs: 12, md: 3 }}>
            <center>Stagione {rendiconto?.stagione}</center>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <center>
              <b>Totale:&nbsp;{formatEuro(totTotaleDovuto() || '-')}</b>
            </center>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <center>
              <b>Pagato:&nbsp;{formatEuro(totTotalePagato() || '-')}</b>
            </center>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <center>
              <b>
                Da pagare:&nbsp;
                {formatEuro(totTotaleDovuto() - totTotalePagato() || '-')}
              </b>
            </center>
          </Grid>
        </Grid>
        {rendicontoQuota?.length > 0 && (
          <Grid container sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={rendicontoQuota}
                  columns={columnsQuota}
                />
                <TableBody>
                  {rendicontoQuota?.map((r, index) => {
                    const aInputField = (field, type) =>
                      rInputField(r, field, x => setRendVoce(x, r), type)

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor:
                            r.importo !== 0 && !r.datePaid && colours.highlight,
                        }}
                      >
                        <TableCell>Quota di segreteria</TableCell>
                        <TableCell sx={widthProps1}>
                          {isView || !admin
                            ? formatEuro(r.importo)
                            : aInputField('importo', 'number')}
                        </TableCell>
                        <TableCell sx={widthProps2}>
                          {isView || !admin ? r.note : aInputField('note')}
                        </TableCell>
                        <TableCell sx={widthProps2}>
                          {aInputField('datePaid', 'date')}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
        {(loadingIscrizioni && <GridCircularProgress />) ||
          (rendicontoIscrizioni?.length > 0 && (
            <Grid container sx={{ width: '100%' }}>
              {fTitle('Iscrizioni')}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={rendicontoIscrizioni}
                    columns={columnsIscrizioni}
                  />
                  <TableBody>
                    {rendicontoIscrizioni?.map((r, index) => {
                      const cellProps = fStyleProps(rendicontoIscrizioni, index)

                      const aInputField = (field, type) =>
                        rInputField(r, field, x => setRendVoce(x, r), type)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              r.importo !== 0 &&
                              !r.datePaid &&
                              colours.highlight,
                          }}
                        >
                          <TableCell {...cellProps}>
                            {nCampionato(r.codiceCampionato) || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.categoria || '??'}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps1}>
                            {isView || !admin
                              ? formatEuro(r.importo)
                              : aInputField('importo', 'number')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {isView || !admin ? r.note : aInputField('note')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {aInputField('datePaid', 'date')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        {(loadingSpostamenti && <GridCircularProgress />) ||
          (rendicontoSpostamenti?.length > 0 && (
            <Grid container sx={{ width: '100%' }}>
              {fTitle('Spostamenti')}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={rendicontoSpostamenti}
                    columns={columnsSpostamenti}
                  />
                  <TableBody>
                    {rendicontoSpostamenti?.map((r, index) => {
                      const cellProps = fStyleProps(
                        rendicontoSpostamenti,
                        index,
                      )

                      const aInputField = (field, type) =>
                        rInputField(r, field, x => setRendVoce(x, r), type)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              r.importo !== 0 &&
                              !r.datePaid &&
                              colours.highlight,
                          }}
                        >
                          <TableCell {...cellProps}>
                            {nCampionato(r.codiceCampionato) || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.categoria || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.numeroGara || '??'}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps1}>
                            {isView || !admin
                              ? formatEuro(r.importo)
                              : aInputField('importo', 'number')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {isView || !admin ? r.note : aInputField('note')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {aInputField('datePaid', 'date')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        {(loadingGareArbitro && <GridCircularProgress />) ||
          (rendicontoGareArbitro?.length > 0 && (
            <Grid container sx={{ width: '100%' }}>
              {fTitle('Tasse gara')}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={rendicontoGareArbitro}
                    columns={columnsGareArbitro}
                  />
                  <TableBody>
                    {rendicontoGareArbitro?.map((r, index) => {
                      const cellProps = fStyleProps(
                        rendicontoGareArbitro,
                        index,
                      )

                      const aInputField = (field, type) =>
                        rInputField(r, field, x => setRendVoce(x, r), type)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              r.importo !== 0 &&
                              !r.datePaid &&
                              colours.highlight,
                          }}
                        >
                          <TableCell {...cellProps}>
                            {nCampionato(r.codiceCampionato) || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.categoria || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.numeroGara || '??'}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps1}>
                            {isView || !admin
                              ? formatEuro(r.importo)
                              : aInputField('importo', 'number')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {isView || !admin ? r.note : aInputField('note')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {aInputField('datePaid', 'date')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        {(loadingGareRitardo && <GridCircularProgress />) ||
          (rendicontoRisultatiRitardo?.length > 0 && (
            <Grid container sx={{ width: '100%' }}>
              {fTitle('Sanzioni per risultati inviati in ritardo')}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={rendicontoRisultatiRitardo}
                    columns={columnsRisultatiRitardo}
                  />
                  <TableBody>
                    {rendicontoRisultatiRitardo?.map((r, index) => {
                      const cellProps = fStyleProps(
                        rendicontoRisultatiRitardo,
                        index,
                      )

                      const aInputField = (field, type) =>
                        rInputField(r, field, x => setRendVoce(x, r), type)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              r.importo !== 0 &&
                              !r.datePaid &&
                              colours.highlight,
                          }}
                        >
                          <TableCell {...cellProps}>
                            {nCampionato(r.codiceCampionato) || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.categoria || '??'}
                          </TableCell>
                          <TableCell {...cellProps}>
                            {r.numeroGara || '??'}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps1}>
                            {isView || !admin
                              ? formatEuro(r.importo)
                              : aInputField('importo', 'number')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {isView || !admin ? r.note : aInputField('note')}
                          </TableCell>
                          <TableCell {...cellProps} sx={widthProps2}>
                            {aInputField('datePaid', 'date')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        {(loadingSanzioni && <GridCircularProgress />) ||
          (rendicontoSanzioni?.length > 0 && (
            <Grid container sx={{ width: '100%' }}>
              {fTitle('Sanzioni')}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={rendicontoSanzioni}
                    columns={columnsSanzioni}
                  />
                  <TableBody>
                    {rendicontoSanzioni?.map((r, index) => {
                      const aInputField = (field, type) =>
                        rInputField(r, field, x => setRendVoce(x, r), type)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              r.importo !== 0 &&
                              !r.datePaid &&
                              colours.highlight,
                          }}
                        >
                          <TableCell>{r.descrizione || '??'}</TableCell>
                          <TableCell sx={widthProps1}>
                            {isView || !admin
                              ? formatEuro(r.importo)
                              : aInputField('importo', 'number')}
                          </TableCell>
                          <TableCell sx={widthProps2}>
                            {isView || !admin ? r.note : aInputField('note')}
                          </TableCell>
                          <TableCell sx={widthProps2}>
                            {aInputField('datePaid', 'date')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        {(loadingAcconti && <GridCircularProgress />) ||
          (rendicontoAcconti?.length > 0 && (
            <Grid container sx={{ width: '100%' }}>
              {fTitle('Acconti')}
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={rendicontoAcconti}
                    columns={columnsAcconti}
                  />
                  <TableBody>
                    {rendicontoAcconti?.map((r, index) => {
                      const aInputField = (field, type) =>
                        rInputField(r, field, x => setRendVoce(x, r), type)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor:
                              r.importo !== 0 &&
                              !r.datePaid &&
                              colours.highlight,
                          }}
                        >
                          <TableCell>{r.descrizione || '??'}</TableCell>
                          <TableCell sx={widthProps1}>
                            {isView || !admin
                              ? formatEuro(r.importo)
                              : aInputField('importo', 'number')}
                          </TableCell>
                          <TableCell sx={widthProps2}>
                            {isView || !admin ? r.note : aInputField('note')}
                          </TableCell>
                          <TableCell sx={widthProps2}>
                            {aInputField('datePaid', 'date')}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ))}
        {admin && (
          <Grid container alignItems='center' sx={{ width: '100%' }}>
            {newSanzione ? (
              <>
                <Grid size={10}>
                  <center>
                    <InputField
                      id='descrizione'
                      value={newSanzione}
                      onChange={setNewSanzione}
                      label={columnsSanzioni?.[0].label}
                    />
                  </center>
                </Grid>
                <GridIconButton
                  type='save'
                  size={1}
                  onClick={() => {
                    setRendiconto({
                      ...rendiconto,
                      voci: [
                        ...rendiconto?.voci,
                        {
                          _id: new Date().getTime(), // temporary id, will be reset by the backend
                          tipologia: 'sanzione',
                          descrizione: newSanzione,
                        },
                      ],
                    })
                    setNewSanzione()
                  }}
                />
                <GridIconButton
                  type='clear'
                  size={1}
                  onClick={() => setNewSanzione()}
                />
              </>
            ) : (
              <GridIconButton
                type='add'
                size={12}
                onClick={() =>
                  setNewSanzione(
                    'Comunicato XX del DD/MM/YYYY, provvedimento NN',
                  )
                }
                text='Aggiungi sanzione'
              />
            )}
          </Grid>
        )}
        {admin && (
          <Grid container alignItems='center' sx={{ width: '100%' }}>
            {newAcconto ? (
              <>
                <Grid size={10}>
                  <center>
                    <InputField
                      id='descrizione'
                      value={newAcconto}
                      onChange={setNewAcconto}
                      label={columnsAcconti?.[0].label}
                    />
                  </center>
                </Grid>
                <GridIconButton
                  type='save'
                  size={1}
                  onClick={() => {
                    setRendiconto({
                      ...rendiconto,
                      voci: [
                        ...rendiconto?.voci,
                        {
                          _id: new Date().getTime(), // temporary id, will be reset by the backend
                          tipologia: 'acconto',
                          descrizione: newAcconto,
                        },
                      ],
                    })
                    setNewAcconto()
                  }}
                />
                <GridIconButton
                  type='clear'
                  size={1}
                  onClick={() => setNewAcconto()}
                />
              </>
            ) : (
              <GridIconButton
                type='add'
                size={12}
                onClick={() => setNewAcconto('Acconto')}
                text='Aggiungi acconto'
              />
            )}
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export default DialogRendiconto
